import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Database, FolderArchive, Download, CheckCircle, AlertCircle, Loader2, HardDrive, Image, FileText, RefreshCw, Package } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const BackupManagement = () => {
  const [dbProgress, setDbProgress] = useState(0);
  const [assetsProgress, setAssetsProgress] = useState(0);
  const [dbDownloading, setDbDownloading] = useState(false);
  const [assetsDownloading, setAssetsDownloading] = useState(false);
  const [dbStatus, setDbStatus] = useState(null);
  const [assetsStatus, setAssetsStatus] = useState(null);
  
  // Collections state
  const [collections, setCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(true);
  const [updatingCollections, setUpdatingCollections] = useState(false);

  const assetTypes = [
    { icon: Image, name: 'Company Logos', description: 'Employer and Prime Employer company logos', folder: 'logos' },
    { icon: Image, name: 'Client Logos', description: 'Our Clients section logos', folder: 'client_logos' },
    { icon: FileText, name: 'ID Proofs', description: 'Employer and HR ID verification documents', folder: 'id_cards, id_proofs' },
    { icon: Image, name: 'Profile Pictures', description: 'User profile images', folder: 'profiles' },
    { icon: FileText, name: 'CVs/Resumes', description: 'Job seeker uploaded resumes', folder: 'cvs' },
    { icon: Image, name: 'CMS Assets', description: 'Hero images and gallery content', folder: 'cms_assets' },
  ];

  // Fetch collections info on mount
  useEffect(() => {
    fetchCollectionsInfo();
  }, []);

  const fetchCollectionsInfo = async () => {
    setLoadingCollections(true);
    try {
      const response = await api.get('/admin/backup/collections-info');
      setCollections(response.data.collections || []);
    } catch (error) {
      console.error('Failed to fetch collections info:', error);
      toast.error('Failed to load collections info');
    } finally {
      setLoadingCollections(false);
    }
  };

  const handleUpdateCollections = async () => {
    setUpdatingCollections(true);
    try {
      const response = await api.post('/admin/backup/update-collections');
      toast.success(response.data.message || 'Collections updated successfully');
      // Refresh collections list
      await fetchCollectionsInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update collections');
    } finally {
      setUpdatingCollections(false);
    }
  };

  const downloadDatabaseBackup = async () => {
    setDbDownloading(true);
    setDbProgress(0);
    setDbStatus(null);

    try {
      const progressInterval = setInterval(() => {
        setDbProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const response = await api.get('/admin/backup/database', {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setDbProgress(Math.min(percentCompleted, 95));
        }
      });

      clearInterval(progressInterval);
      setDbProgress(100);

      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().split('T')[0];
      link.download = `vvvsi_database_backup_${date}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setDbStatus('success');
      toast.success('Database backup downloaded successfully!');
    } catch (error) {
      console.error('Database backup error:', error);
      setDbStatus('error');
      toast.error('Failed to download database backup');
    } finally {
      setDbDownloading(false);
    }
  };

  const downloadAssetsBackup = async () => {
    setAssetsDownloading(true);
    setAssetsProgress(0);
    setAssetsStatus(null);

    try {
      const progressInterval = setInterval(() => {
        setAssetsProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 5;
        });
      }, 300);

      const response = await api.get('/admin/backup/assets', {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setAssetsProgress(Math.min(percentCompleted, 95));
        }
      });

      clearInterval(progressInterval);
      setAssetsProgress(100);

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().split('T')[0];
      link.download = `vvvsi_assets_backup_${date}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setAssetsStatus('success');
      toast.success('Assets backup downloaded successfully!');
    } catch (error) {
      console.error('Assets backup error:', error);
      setAssetsStatus('error');
      toast.error('Failed to download assets backup');
    } finally {
      setAssetsDownloading(false);
    }
  };

  const getTotalDocuments = () => {
    return collections.reduce((sum, col) => sum + (col.count || 0), 0);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
          Backup Management
        </h1>
        <p className="text-slate-600 mt-2">
          Download complete database and asset backups for disaster recovery
        </p>
      </div>

      {/* Backup Options Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Database Backup */}
        <Card className="p-6 border-slate-200">
          <div className="flex items-start gap-4 mb-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Database className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                Database Backup
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                Export all MongoDB collections as JSON
              </p>
            </div>
          </div>
          
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Collections included:</span>
              <span className="font-medium text-slate-900">{collections.length}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Total documents:</span>
              <span className="font-medium text-slate-900">{getTotalDocuments().toLocaleString()}</span>
            </div>
          </div>

          {dbDownloading && (
            <div className="mb-4">
              <Progress value={dbProgress} className="h-2" />
              <p className="text-xs text-slate-500 mt-1 text-center">{dbProgress}% complete</p>
            </div>
          )}

          {dbStatus && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
              dbStatus === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {dbStatus === 'success' ? (
                <><CheckCircle className="w-4 h-4" /> Backup downloaded successfully</>
              ) : (
                <><AlertCircle className="w-4 h-4" /> Backup failed. Please try again.</>
              )}
            </div>
          )}

          <Button
            onClick={downloadDatabaseBackup}
            disabled={dbDownloading}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="download-db-backup-btn"
          >
            {dbDownloading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Downloading...</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Download Database Backup</>
            )}
          </Button>
        </Card>

        {/* Assets Backup */}
        <Card className="p-6 border-slate-200">
          <div className="flex items-start gap-4 mb-4">
            <div className="bg-amber-100 p-3 rounded-lg">
              <FolderArchive className="w-8 h-8 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                Assets Backup
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                Download all uploaded files as ZIP
              </p>
            </div>
          </div>
          
          <div className="space-y-2 mb-4 max-h-32 overflow-y-auto">
            {assetTypes.map((asset, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <asset.icon className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">{asset.name}</span>
              </div>
            ))}
          </div>

          {assetsDownloading && (
            <div className="mb-4">
              <Progress value={assetsProgress} className="h-2" />
              <p className="text-xs text-slate-500 mt-1 text-center">{assetsProgress}% complete</p>
            </div>
          )}

          {assetsStatus && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
              assetsStatus === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {assetsStatus === 'success' ? (
                <><CheckCircle className="w-4 h-4" /> Assets downloaded successfully</>
              ) : (
                <><AlertCircle className="w-4 h-4" /> Download failed. Please try again.</>
              )}
            </div>
          )}

          <Button
            onClick={downloadAssetsBackup}
            disabled={assetsDownloading}
            className="w-full bg-amber-600 hover:bg-amber-700"
            data-testid="download-assets-backup-btn"
          >
            {assetsDownloading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Downloading...</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Download Assets Backup</>
            )}
          </Button>
        </Card>
      </div>

      {/* Collections Details Section */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Database Collections
            </h2>
            <p className="text-slate-600 text-sm mt-1">
              All MongoDB collections included in backup
            </p>
          </div>
          <Button
            onClick={handleUpdateCollections}
            disabled={updatingCollections || loadingCollections}
            variant="outline"
            className="border-teal-300 text-teal-700 hover:bg-teal-50"
            data-testid="update-collections-btn"
          >
            {updatingCollections ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" /> Update Collections</>
            )}
          </Button>
        </div>

        <Card className="border-slate-200">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-12 text-center">#</TableHead>
                  <TableHead>Collection Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Documents</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingCollections ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                      <p className="text-sm text-slate-500 mt-2">Loading collections...</p>
                    </TableCell>
                  </TableRow>
                ) : collections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                      <p className="text-slate-500">No collections found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  collections.map((col, index) => (
                    <TableRow key={col.name} className="hover:bg-slate-50">
                      <TableCell className="text-center text-slate-500">{index + 1}</TableCell>
                      <TableCell>
                        <code className="text-sm bg-slate-100 px-2 py-1 rounded text-slate-800">{col.name}</code>
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">{col.description || '-'}</TableCell>
                      <TableCell className="text-right font-medium">{(col.count || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          col.count > 0 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {col.count > 0 ? (
                            <><CheckCircle className="w-3 h-3" /> Active</>
                          ) : (
                            'Empty'
                          )}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {collections.length > 0 && (
            <div className="p-4 bg-slate-50 border-t border-slate-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">
                  <strong>{collections.length}</strong> collections | <strong>{getTotalDocuments().toLocaleString()}</strong> total documents
                </span>
                <span className="text-slate-500">
                  Click "Update Collections" to detect new collections
                </span>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Restore Instructions */}
      <Card className="p-6 border-slate-200 bg-slate-50">
        <div className="flex items-start gap-4">
          <div className="bg-slate-200 p-2 rounded-lg">
            <HardDrive className="w-6 h-6 text-slate-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Restore Instructions
            </h3>
            <div className="mt-2 space-y-2 text-sm text-slate-600">
              <p><strong>Database:</strong> JSON file containing all collections. Import to MongoDB using <code className="bg-slate-200 px-1 rounded">mongoimport</code> or MongoDB Compass.</p>
              <p><strong>Assets:</strong> Extract the ZIP file to your uploads directory maintaining the folder structure.</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BackupManagement;
